/**
 * Content Carousel: wraps each top-level inner block in a Swiper slide on the frontend.
 */
import Swiper from 'swiper/bundle';

const BLOCK_SEL = '.content-carousel:not(.is-preview)';
const TRACK_SEL = '.content-carousel__track';

function isEditor() {
  return (
    document.body.classList.contains('block-editor-page')
    || document.body.classList.contains('wp-admin')
    || document.querySelector('.block-editor-writing-flow')
  );
}

function mountSlideStructure(track) {
  if (track.dataset.swiperMounted === 'true') {
    return;
  }

  const children = [...track.children].filter(
    (el) => !el.classList.contains('swiper-slide') && !el.classList.contains('block-list-appender'),
  );

  if (!children.length) {
    return;
  }

  children.forEach((child) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide content-carousel__slide';
    slide.appendChild(child);
    track.appendChild(slide);
  });

  track.dataset.swiperMounted = 'true';
}

function equalizeSlideHeights(swiper) {
  swiper.slides.forEach((slide) => {
    slide.style.height = '';
  });

  const maxHeight = Math.max(
    0,
    ...[...swiper.slides].map((slide) => slide.offsetHeight),
  );

  if (maxHeight === 0) {
    return;
  }

  swiper.slides.forEach((slide) => {
    slide.style.height = `${maxHeight}px`;
  });
}

function buildConfig(block, slideCount) {
  const desktop = Math.max(1, Math.min(6, parseInt(block.dataset.slidesPerView || '3', 10)));
  const tablet = Math.min(1, desktop);
  const spaceBetween = Math.max(0, parseInt(block.dataset.spaceBetween || '20', 10));
  const showArrows = block.dataset.showArrows === '1';
  const showDots = block.dataset.showDots === '1';
  const enableAutoplay = block.dataset.enableAutoplay === '1';
  const enableLoop = block.dataset.enableLoop === '1';

  const config = {
    slidesPerView: 1,
    spaceBetween,
    speed: 500,
    grabCursor: true,
    watchOverflow: true,
    loop: enableLoop && slideCount > desktop,
    a11y: { enabled: true },
    breakpoints: {
      640: {
        slidesPerView: tablet,
      },
      1024: {
        slidesPerView: desktop,
      },
    },
    on: {
      init: equalizeSlideHeights,
      resize: equalizeSlideHeights,
      breakpoint: equalizeSlideHeights,
      imagesReady: equalizeSlideHeights,
    },
  };

  if (showArrows && slideCount > 1) {
    config.navigation = {
      nextEl: block.querySelector('.content-carousel__nav-btn--next'),
      prevEl: block.querySelector('.content-carousel__nav-btn--prev'),
    };
  }

  if (showDots && slideCount > 1) {
    config.pagination = {
      el: block.querySelector('.content-carousel__pagination'),
      clickable: true,
    };
  }

  if (enableAutoplay && slideCount > 1) {
    config.autoplay = {
      delay: 5000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    };
  }

  return config;
}

function initBlock(block) {
  if (isEditor()) {
    return;
  }

  const track = block.querySelector(TRACK_SEL);
  if (!track) {
    return;
  }

  mountSlideStructure(track);

  const slideCount = track.querySelectorAll('.swiper-slide').length;
  if (slideCount === 0) {
    return;
  }

  const swiperEl = block.querySelector('.content-carousel__swiper');
  if (!swiperEl) {
    return;
  }

  new Swiper(swiperEl, buildConfig(block, slideCount));
}

function initContentCarousels() {
  document.querySelectorAll(BLOCK_SEL).forEach(initBlock);
}

function whenReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    queueMicrotask(fn);
  }
}

whenReady(initContentCarousels);

if (typeof window.acf !== 'undefined') {
  window.acf.addAction('render_block_preview/type=content-carousel', initContentCarousels);
  window.acf.addAction('render_block_preview/type=acf/content-carousel', initContentCarousels);
}
